<script setup lang="ts">
import { computed, useSlots } from "vue";
import useSelectionControl, {
  type ISelectionControlEmit,
} from "../../composables/useSelectionControl";
import type { IChipsProps, IChipsEmits } from "./VChips.types";

const props = withDefaults(defineProps<IChipsProps>(), {
  wrapperClass: undefined,
  value: true,
  type: "checkbox",
});

const emit = defineEmits<IChipsEmits>();
defineOptions({ inheritAttrs: false });

const slots = useSlots();

const rootClass = computed(() => [
  "pl-chips",
  props.wrapperClass,
  {
    "pl-chips_disabled": props.disabled,
    "pl-chips_left-icon": !!slots.icon,
  },
]);

const { onChange, isChecked } = useSelectionControl(
  props,
  emit as ISelectionControlEmit,
);
</script>

<template>
  <label :class="rootClass">
    <input
      v-bind="$attrs"
      :disabled="disabled"
      :checked="isChecked"
      class="pl-chips__field"
      :type="type"
      @change="onChange"
    />
    <span class="pl-chips__decorator">
      <span v-if="$slots.icon" class="pl-chips__icon">
        <slot name="icon" />
      </span>
      <span v-if="$slots.default" class="pl-chips__text">
        <slot />
      </span>
    </span>
  </label>
</template>

<style lang="postcss" scoped>
.pl-chips {
  position: relative;
  display: inline-flex;
  user-select: none;
  vertical-align: top;

  & &__field {
    top: 0;
    left: 0;
    border: 0;
    opacity: 0;
    margin: 0;
    position: absolute;
    cursor: pointer;
    width: 100%;
    height: 100%;

    &:disabled {
      cursor: not-allowed;
    }
  }

  & &__icon {
    display: inline-flex;

    :deep(svg) {
      /* Помним, про договоренность не скейлить иконки, но прописываем размеры,
      ибо при обновлении концепции ДС, тут сменили используемый размер иконок с 24px на 20px,
      а возможности пройти по всем проектам и в моменте поменять иконки возможности нет, поэтому устанавливаем размер,
      дабы заскейлить все иконки до размера, который необходимом этому компоненту
      */
      width: var(--pl-unit-x5);
      height: auto;
    }
  }

  &_disabled {
    cursor: not-allowed;
  }
}

.pl-chips__decorator {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--pl-text-primary);
  background: var(--pl-chips-background-unselected-default);
  border-radius: var(--pl-chips-radius-s);
  padding: 0 var(--pl-unit-x3);
  height: var(--pl-unit-x8);
  gap: var(--pl-unit-x1);
  transition:
    background-color 0.2s,
    color 0.2s;

  .pl-chips_left-icon & {
    padding-left: var(--pl-unit-x2);
  }

  .pl-chips__field:hover + & {
    background: var(--pl-chips-background-unselected-hover);
  }

  .pl-chips__field:active + & {
    background: var(--pl-chips-background-unselected-pressed);
  }

  .pl-chips__field:checked + & {
    background: var(--pl-chips-background-selected-default);
    color: var(--pl-text-invert-primary);
  }

  .pl-chips__field:focus-visible + & {
    outline: 2px solid var(--pl-tabulation-border-focused);
    outline-offset: 2px;
  }

  .pl-chips__field:disabled + & {
    background-color: var(--pl-chips-background-unselected-disabled);
    color: var(--pl-text-tertiary);
  }

  .pl-chips__field:disabled:checked + & {
    background-color: var(--pl-chips-background-unselected-disabled);
  }

  .pl-chips:hover .pl-chips__field:checked + & {
    background: var(--pl-chips-background-selected-hover);
  }

  .pl-chips:hover .pl-chips__field:active:checked + & {
    background: var(--pl-chips-background-selected-pressed);
  }

  .pl-chips_disabled:hover .pl-chips__field:disabled:checked + & {
    background-color: var(--pl-chips-background-unselected-disabled);
  }
}

.pl-chips__text {
  font: var(--pl-font-body-small-accent);
  white-space: nowrap;
}
</style>
